import { ArrowLeft, Wallet, TrendUp } from "@phosphor-icons/react";
import { formatCurrency } from "@/lib/format";
import { SubMetric } from "./Atoms";

export function KpiBlock({ label, sublabel, metric, accent, icon, testId }) {
  const m = metric || { total: 0, per_unit: 0, per_sf: 0 };
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={testId}>
      <div className="h-1 w-full" style={{ background: accent }} />
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
            {sublabel && (
              <span className="text-[10px] font-medium text-slate-400 mt-0.5 normal-case tracking-normal">{sublabel}</span>
            )}
          </div>
          <span style={{ color: accent }}>{icon}</span>
        </div>
        <div className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 tabular-nums">
          {formatCurrency(m.total)}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 pt-4 border-t border-slate-100">
          <SubMetric label="Per Unit" value={m.per_unit} />
          <SubMetric label="Per SF" value={m.per_sf} />
        </div>
      </div>
    </div>
  );
}

export function SpendToDateCard({ metric, project, testId }) {
  const m = metric || { total: 0, paid: 0, unpaid: 0 };
  const unitCount = project?.unit_count || 0;
  const squareFeet = project?.square_feet || 0;
  // The headline value now reflects only what has been PAID to date.
  // The unpaid balance is shown as "Payables" beneath.
  const perUnit = unitCount ? m.paid / unitCount : 0;
  const perSf = squareFeet ? m.paid / squareFeet : 0;
  const accent = "#327de3";
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={testId}>
      <div className="h-1 w-full" style={{ background: accent }} />
      <div className="p-6">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Total Spend to Date</span>
          <span style={{ color: accent }}><Wallet size={18} weight="bold" /></span>
        </div>
        <div className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 tabular-nums">
          {formatCurrency(m.paid)}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 pt-4 border-t border-slate-100">
          <SubMetric label="Paid" value={m.paid} />
          <SubMetric label="Payables" value={m.unpaid} />
          <SubMetric label="Per Unit" value={perUnit} />
          <SubMetric label="Per SF" value={perSf} />
        </div>
      </div>
    </div>
  );
}

export function GrossRevenueCard({ metric, project, onClick, testId }) {
  const m = metric || {};
  const accent = "#84CC16";
  const sections = [
    { key: "closed", label: "Closed to Date",  v: m.closed_to_date || {}, color: "#0E7036" },
    { key: "ic",     label: "In Contract",     v: m.in_contract || {},    color: "#F59E0B" },
    { key: "rem",    label: "Remaining Sales", v: m.remaining_sales || {},color: "#5E6A78" },
  ];
  const totalAll = m.total || 0;
  const unitCount = project?.unit_count || 0;
  const squareFeet = project?.square_feet || 0;
  const projectedPerUnit = unitCount ? totalAll / unitCount : 0;
  const projectedPerSf = squareFeet ? totalAll / squareFeet : 0;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      className="text-left bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:border-slate-300 hover:shadow transition-all cursor-pointer w-full"
      data-testid={testId}
    >
      <div className="h-1 w-full" style={{ background: accent }} />
      <div className="p-2.5 sm:p-6">
        <div className="flex items-center justify-between mb-2.5 sm:mb-3">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Gross Revenue</span>
          <span style={{ color: "#65A30D" }}><TrendUp size={18} weight="bold" /></span>
        </div>

        <div className="grid grid-cols-[1.3fr_1fr_0.8fr_0.65fr] sm:grid-cols-[1.4fr_1.2fr_1fr_1fr] gap-1 sm:gap-4 text-[7px] sm:text-[10px] font-semibold uppercase tracking-[0em] sm:tracking-[0.12em] text-slate-400 pb-1.5 sm:pb-2 border-b border-slate-100">
          <span />
          <span className="text-right">Total</span>
          <span className="text-right">Avg / Unit</span>
          <span className="text-right">Avg / SF</span>
        </div>

        {sections.map((s, i) => (
          <div
            key={s.key}
            className={`grid grid-cols-[1.3fr_1fr_0.8fr_0.65fr] sm:grid-cols-[1.4fr_1.2fr_1fr_1fr] gap-1 sm:gap-4 items-center py-1.5 sm:py-2 ${i < sections.length - 1 ? "border-b border-slate-100" : ""}`}
            data-testid={`gp-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="flex items-center gap-1 sm:gap-2 min-w-0">
              <span className="w-[2px] sm:w-1.5 h-3.5 sm:h-5 rounded-sm shrink-0" style={{ background: s.color }} />
              <span className="text-[8px] sm:text-xs font-semibold uppercase tracking-[0em] sm:tracking-[0.08em] text-slate-700 leading-tight">
                {s.label}
                {Number.isFinite(s.v.count) && s.v.count > 0 ? (
                  <span className="ml-0.5 sm:ml-1 text-slate-900 normal-case font-bold">· {s.v.count}</span>
                ) : null}
              </span>
            </div>
            <span className="text-right text-[10px] sm:text-sm font-bold text-slate-900 tabular-nums">{formatCurrency(s.v.total || 0)}</span>
            <span className="text-right text-[9px] sm:text-sm text-slate-600 tabular-nums">{formatCurrency(s.v.avg_per_unit || 0)}</span>
            <span className="text-right text-[9px] sm:text-sm text-slate-600 tabular-nums">{formatCurrency(s.v.avg_per_sf || 0)}</span>
          </div>
        ))}

        <div className="grid grid-cols-[1.3fr_1fr_0.8fr_0.65fr] sm:grid-cols-[1.4fr_1.2fr_1fr_1fr] gap-1 sm:gap-4 items-center pt-2 sm:pt-3 border-t border-slate-200">
          <span className="font-display text-xs sm:text-xl font-extrabold tracking-tight text-slate-900">Projected</span>
          <span className="text-right font-display text-[11px] sm:text-xl font-bold text-slate-900 tabular-nums">{formatCurrency(totalAll)}</span>
          <div className="text-right">
            <div className="text-[7px] sm:text-[10px] font-semibold uppercase tracking-[0em] sm:tracking-[0.12em] text-slate-400 leading-tight">Per Unit</div>
            <div className="text-[9px] sm:text-sm font-semibold text-slate-700 tabular-nums">{formatCurrency(projectedPerUnit)}</div>
          </div>
          <div className="text-right">
            <div className="text-[7px] sm:text-[10px] font-semibold uppercase tracking-[0em] sm:tracking-[0.12em] text-slate-400 leading-tight">Per SF</div>
            <div className="text-[9px] sm:text-sm font-semibold text-slate-700 tabular-nums">{formatCurrency(projectedPerSf)}</div>
          </div>
        </div>

        <div className="mt-3 text-[10px] text-[#327de3] font-semibold flex items-center gap-1">
          Click anywhere to view Revenue table <ArrowLeft size={10} className="rotate-[-90deg]" />
        </div>
      </div>
    </div>
  );
}

export function ProfitProjectionCard({ spendToDate, totalBudgeted, grossProceeds, project }) {
  const revenueToDate = grossProceeds?.closed_to_date?.total || 0;
  // Expense to date now reflects only what has actually been PAID, matching
  // the Total Spend to Date headline figure.
  const expenseToDate = spendToDate?.paid || 0;
  const profitToDate = revenueToDate - expenseToDate;

  const revenueProjected = grossProceeds?.total || 0;
  const expenseProjected = totalBudgeted?.total || 0;
  const profitProjected = revenueProjected - expenseProjected;

  const unitCount = project?.unit_count || 0;
  const squareFeet = project?.square_feet || 0;
  const perUnit = (v) => (unitCount ? v / unitCount : 0);
  const perSf = (v) => (squareFeet ? v / squareFeet : 0);

  const rows = [
    { label: "Revenue", toDate: revenueToDate, projected: revenueProjected },
    { label: "Expense", toDate: expenseToDate, projected: expenseProjected },
    { label: "Profit",  toDate: profitToDate,  projected: profitProjected, separator: true, emphasize: true },
  ];

  const accent = "#327de3";
  return (
    <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="kpi-profit-projection">
      <div className="h-1 w-full" style={{ background: accent }} />
      <div className="p-3 sm:p-6">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Profit Projection</span>
          <span style={{ color: accent }}><TrendUp size={18} weight="bold" /></span>
        </div>

        <div className="grid grid-cols-[1fr_1.1fr_16px_1.1fr_0.9fr_0.7fr] sm:grid-cols-[1fr_1.1fr_24px_1.1fr_0.9fr_0.7fr] gap-1.5 sm:gap-3 text-[8px] sm:text-[9px] font-semibold uppercase tracking-[0.08em] sm:tracking-[0.14em] text-slate-400 pb-2 border-b border-slate-100">
          <span />
          <span className="text-right text-slate-600">To Date</span>
          <span />
          <span className="text-right text-slate-900 font-bold tracking-[0.06em] sm:tracking-[0.1em]">Projected</span>
          <span className="text-right">Per Unit</span>
          <span className="text-right">Per SF</span>
        </div>

        {rows.map((r) => {
          const profitColor = r.emphasize
            ? (r.projected >= 0 ? "text-[#0E7036]" : "text-[#D92D20]")
            : "text-slate-900";
          return (
            <div
              key={r.label}
              className={`grid grid-cols-[1fr_1.1fr_16px_1.1fr_0.9fr_0.7fr] sm:grid-cols-[1fr_1.1fr_24px_1.1fr_0.9fr_0.7fr] gap-1.5 sm:gap-3 items-center py-2 sm:py-2.5 border-b border-slate-100`}
            >
              <span className={`text-[10px] sm:text-xs uppercase tracking-[0.04em] sm:tracking-[0.1em] ${r.emphasize ? "font-bold text-slate-900" : "font-semibold text-slate-700"}`}>{r.label}</span>
              <span className={`text-right tabular-nums ${r.emphasize ? `text-xs sm:text-base font-bold ${profitColor}` : "text-[11px] sm:text-sm font-medium text-slate-900"}`}>
                {formatCurrency(r.toDate)}
              </span>
              <span className="flex justify-center"><span className="w-px h-4 sm:h-5 bg-slate-200" /></span>
              <span className={`text-right tabular-nums ${r.emphasize ? `text-xs sm:text-base font-bold ${profitColor}` : "text-[11px] sm:text-sm font-medium text-slate-900"}`}>
                {formatCurrency(r.projected)}
              </span>
              <span className={`text-right tabular-nums ${r.emphasize ? `text-[11px] sm:text-sm font-bold ${profitColor}` : "text-[10px] sm:text-xs text-slate-600"}`}>
                {formatCurrency(perUnit(r.projected))}
              </span>
              <span className={`text-right tabular-nums ${r.emphasize ? `text-[11px] sm:text-sm font-bold ${profitColor}` : "text-[10px] sm:text-xs text-slate-600"}`}>
                {formatCurrency(perSf(r.projected))}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
